import { Piece } from "./Piece";
import { PieceType } from "./PieceType";
import { Position } from "./Position";

export interface MoveFromParent {
  parentPieceIndex: number;
  deltaX: number;
  deltaY: number;
}

export class Game {
  private parent: Game | null = null;
  private parentMove: MoveFromParent | null = null;

  constructor(
    public readonly pieces: readonly Piece[],
    public width = 4,
    public height = 5,
    public lastPieceMove: Piece | null = null,
    public distance = 0,
  ) {}

  public get parentGame(): Game | null {
    return this.parent;
  }

  public get moveFromParent(): MoveFromParent | null {
    return this.parentMove;
  }

  public setParent(parent: Game, move: MoveFromParent): void {
    this.parent = parent;
    this.parentMove = move;
  }

  public canMoveTo(piece: Piece, deltaX: number, deltaY: number): boolean {
    if (
      !piece.positions.every((p) => {
        const newX = p.x + deltaX;
        const newY = p.y + deltaY;
        if (newX < 0 || newX >= this.width) {
          return false;
        }
        if (newY < 0 || newY >= this.height) {
          return false;
        }
        return true;
      })
    ) {
      return false;
    }

    return piece.positions.every((p) => {
      const x = p.x + deltaX;
      const y = p.y + deltaY;
      return this.pieces.every((p) => p === piece || !p.isAt(x, y));
    });
  }

  public isWinningGame(targetX: number, targetY: number): boolean {
    return this.pieces.some((p) => p.isMain && p.isAtExact(targetX, targetY));
  }

  public gethash(withLastPieceMove: boolean): string {
    const occupations: string[] = new Array<string>();
    for (let i = 0; i < this.width * this.height; i++) {
      occupations.push(".");
    }
    this.pieces.forEach((p) => {
      occupations[p.position.x + p.position.y * this.width] = p.isMain
        ? p.pieceType.toUpperCase()
        : p.pieceType;
    });
    const lastPieceMove = this.lastPieceMove;
    if (withLastPieceMove && null !== lastPieceMove) {
      occupations.push(
        lastPieceMove.position.x + "," + lastPieceMove.position.y,
      );
    }
    return occupations.join("");
  }

  public static getGridFromHash(
    hash: string,
    width: number,
    height: number,
  ): string {
    const grid: string[] = [];
    for (let y = 0; y < height; y++) {
      grid.push(hash.slice(y * width, (y + 1) * width));
    }
    return grid.join("\n");
  }

  public static fromHash(hash: string, width = 4, height = 5): Game {
    const pieces: Piece[] = new Array<Piece>();
    hash.split("").forEach((c, i) => {
      if (c === ".") {
        return;
      }
      const x = i % width;
      const y = Math.floor(i / width);
      const pos = new Position(x, y);

      const allowedPieces = Object.values(PieceType);
      const pieceType: PieceType | null =
        allowedPieces.find((p) => p === c.toLowerCase()) || null;

      if (pieceType === null) {
        return;
      }
      pieces.push(new Piece(pos, pieceType, c === c.toUpperCase()));
    });
    return new Game(pieces, width, height);
  }

  public cloneAndMove(
    piece: Piece,
    deltaX: number,
    deltaY: number,
    distance: number,
  ): Game {
    let lastPieceMove: Piece | null = null;
    const pieces: Piece[] = this.pieces.map((p) => {
      const p2 = p.clone();
      if (p === piece) {
        p2.move(deltaX, deltaY);
        lastPieceMove = p2;
      }
      return p2;
    });
    return new Game(pieces, this.width, this.height, lastPieceMove, distance);
  }
}
